/**
 * Κατάλογος ταμείου — «Νέα εμφάνιση» (11/10/2026).
 *
 * Οι κατηγορίες μένουν ΠΑΝΤΑ επάνω ως φίλτρο και τα προϊόντα της επιλεγμένης
 * φαίνονται από κάτω: ένα πάτημα για αλλαγή κατηγορίας, όχι «μπες–βγες».
 * Φαίνονται ΟΛΕΣ (12/10/2026: όχι «Περισσότερες»)· πάνω από τις σειρές της συσκευής
 * το πλέγμα κυλά. Χρώμα ανά κατηγορία κατά τη ρύθμιση της συσκευής (catalogPrefsV2)·
 * οι κάρτες έχουν όνομα και τιμή ΣΤΟ ΚΕΝΤΡΟ, χωρίς περιγραφή και χωρίς «+» (απόφαση ιδιοκτήτη 15/10/2026· η
 * αναζήτηση βρίσκει ακόμη και από την περιγραφή).
 *
 * Καθαρά παρουσίαση: τι είναι ορατό/διαθέσιμο το αποφασίζει το ProductCatalog
 * (κρυφές κατηγορίες, αγαπημένα) και τις επιλογές τις κρατά ο γονέας. Πατιέται
 * ΟΛΗ η κάρτα· το «+» είναι μόνο ένδειξη.
 */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, LayoutGrid, Loader2, Package, Star } from 'lucide-react';
import type { Product, Category } from '../../types/order';
import type { CatalogView } from './ProductCatalog';
import { categoryToneOf, getGridClass } from '../../utils/catalogHelpers';
import { catalogTone } from '../../utils/catalogColors';
import { formatEuro } from '../../utils/formatEuro';
import { useCatalogPrefsV2 } from '../../utils/catalogPrefsV2';
import { keepTouchClickOn } from '../../utils/ghostClick';

// Ύψος κουτιού κατηγορίας (3.25rem = 52px, «λίγο ψηλότερες» 12/10) και κενό (gap-2): το πλέγμα κυλά
// πέρα από `categoryRows` σειρές.
const TILE_REM = 3.25;
const GAP_REM = 0.5;

/* Η απόχρωση της κατηγορίας: ο τόνος που όρισε ο διαχειριστής (category.color, migration 264),
 * αλλιώς από το ΟΝΟΜΑ όπως και στο κλασικό («καφ» → amber) — catalogHelpers.categoryToneOf. Το CSS
 * (CashierV2.css §4, `.xv2-category[data-tone]`) τη βάφει απαλή ή γεμάτη, σε ανοιχτό και σκούρο.
 * Το προϊόν με δικό του τόνο (product.color) παίρνει λωρίδα + απαλή απόχρωση (§5, `.xv2-product[data-tone]`). */

/** Μεγέθη κάρτας ανά πλήθος στηλών (όνομα και τιμή στο κέντρο, 15/10/2026). */
const tileSizing = (columns: number) => {
  if (columns <= 3) return { pad: 'p-4', minH: 'min-h-[7rem]', name: 'text-[15px]', price: 'text-[17px]' };
  if (columns === 4) return { pad: 'p-3.5', minH: 'min-h-[6.5rem]', name: 'text-[15px]', price: 'text-base' };
  if (columns === 5) return { pad: 'p-3', minH: 'min-h-[6.25rem]', name: 'text-sm', price: 'text-[15px]' };
  return { pad: 'p-2.5', minH: 'min-h-[5.5rem]', name: 'text-[13px]', price: 'text-sm' };
};

type Selection = 'favorites' | 'all' | number;

/** Αναζήτηση χωρίς τόνους και κεφαλαία: «κουλουρι» βρίσκει «ΚΟΥΛΟΎΡΙ», «καφε» τον «Καφέ». */
export const searchKey = (s: string | null | undefined): string =>
  (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/ς/g, 'σ');

interface ProductCatalogV2Props {
  /** Ορατές κατηγορίες πρώτου επιπέδου (χωρίς system/κρυφές). */
  categories: Category[];
  /** Ορατά προϊόντα (χωρίς όσα ανήκουν σε κρυφή κατηγορία). */
  products: Product[];
  favoriteProducts: Product[];
  loading: boolean;
  currentView: CatalogView;
  selectedCategory: number | null;
  selectedSubcategory: number | null;
  favoritesSelected: boolean;
  searchTerm: string;
  categoryColumns: number;
  productColumns: number;
  onCategorySelect: (category: Category) => void;
  onSubcategorySelect: (subcategory: Category | null) => void;
  onFavoritesSelect?: () => void;
  onAllProductsSelect?: () => void;
  onProductClick: (product: Product) => void;
  onProductMouseDown: (product: Product) => void;
  onProductMouseUp: () => void;
  getProductPrice: (product: Product) => number;
  getPlasticFeeAddon: (product: Product) => number;
  translationNamespace: string;
  /** Πόσα τεμάχια κάθε προϊόντος είναι ήδη στο καλάθι — μετρητής «×2» στην κάρτα. */
  basketCounts?: Record<number, number>;
}

const ProductCatalogV2: React.FC<ProductCatalogV2Props> = ({
  categories,
  products,
  favoriteProducts,
  loading,
  currentView,
  selectedCategory,
  selectedSubcategory,
  favoritesSelected,
  searchTerm,
  categoryColumns,
  productColumns,
  onCategorySelect,
  onSubcategorySelect,
  onFavoritesSelect,
  onAllProductsSelect,
  onProductClick,
  onProductMouseDown,
  onProductMouseUp,
  getProductPrice,
  getPlasticFeeAddon,
  translationNamespace,
  basketCounts,
}) => {
  const { t } = useTranslation([translationNamespace, 'products', 'common']);
  // Κοινά κείμενα καταλόγου (common:catalogV2) — ίδια σε take away και dine-in.
  const tv = (key: string, opts?: Record<string, unknown>) => t(`common:catalogV2.${key}`, opts);
  const prefs = useCatalogPrefsV2();
  const categoriesRef = useRef<HTMLDivElement>(null);

  const hasFavorites = !!onFavoritesSelect && favoriteProducts.length > 0;
  const isSearching = !!searchTerm.trim();

  // Τι είναι επιλεγμένο. Στην αρχή (καμία επιλογή) δείχνουμε τα αγαπημένα, αλλιώς όλα.
  const selection: Selection = favoritesSelected
    ? 'favorites'
    : selectedCategory
    ? selectedCategory
    : currentView === 'categories'
    ? (hasFavorites ? 'favorites' : 'all')
    : 'all';
  const activeCategory = typeof selection === 'number' ? categories.find(c => c.id === selection) : undefined;
  const visibleSubcategories = (activeCategory?.subcategories || []).filter(s => !s.is_hidden);
  // Στην προβολή «subcategories» δεν είναι επιλεγμένη υποκατηγορία: φαίνεται η ίδια η κατηγορία.
  const activeSubcategory = currentView === 'products' ? selectedSubcategory : null;
  // Υποκατηγορίες ΩΣ ΒΗΜΑ (απόφαση ιδιοκτήτη 15/10/2026): κατηγορία με υποκατηγορίες αντικαθιστά το
  // πλέγμα με [← Κατηγορίες][η κατηγορία][υποκατηγορίες], και η κατηγορία δείχνει ΜΟΝΟ τα δικά της
  // προϊόντα. Ως τώρα: δεύτερη σειρά με «Όλα» που έβγαζε κατηγορία + όλες τις υποκατηγορίες μαζί — στους
  // Κουλουράδες το «ΝΕΡΌ 500ML» χανόταν ανάμεσα σε 67 προϊόντα του «ΨΥΓΕΙΟ ΑΝΑΨΥΚΤΙΚΩΝ».
  const backToCategories = hasFavorites ? onFavoritesSelect : onAllProductsSelect;
  const stepped = !isSearching && !!activeCategory && visibleSubcategories.length > 0 && !!backToCategories;

  const shownProducts: Product[] = (() => {
    if (isSearching) {
      // Και από το όνομα της κατηγορίας: «καφ» φέρνει όλους τους καφέδες.
      const needle = searchKey(searchTerm.trim());
      const matchingCategories = new Set<number>();
      categories.forEach(c => {
        if (searchKey(c.name).includes(needle)) {
          matchingCategories.add(c.id);
          (c.subcategories || []).forEach(sub => matchingCategories.add(sub.id));
        }
      });
      return products.filter(p => searchKey(p.name).includes(needle)
        || searchKey(p.description).includes(needle)
        || matchingCategories.has(p.category_id));
    }
    if (selection === 'favorites') return favoriteProducts;
    if (selection === 'all') return products;
    if (activeSubcategory) return products.filter(p => p.category_id === activeSubcategory);
    // Βήμα υποκατηγοριών: μόνο τα προϊόντα της ίδιας της κατηγορίας.
    if (stepped) return products.filter(p => p.category_id === selection);
    const ids = new Set([selection, ...visibleSubcategories.map(s => s.id)]);
    return products.filter(p => ids.has(p.category_id));
  })();

  const sectionTitle = isSearching
    ? tv('searchResults', { term: searchTerm.trim() })
    : selection === 'favorites'
    ? tv('favorites')
    : selection === 'all'
    ? tv('allProducts')
    : [activeCategory?.name, visibleSubcategories.find(s => s.id === activeSubcategory)?.name].filter(Boolean).join(' · ');

  // Κουτιά κατηγοριών: [Αγαπημένα] + ΟΛΕΣ οι κατηγορίες (+ «Όλα», αν το θέλει η συσκευή).
  // Τίποτα δεν κρύβεται: πάνω από `categoryRows` σειρές το πλέγμα κυλά (απόφαση 12/10/2026).
  type Tile = { key: string; label: string; selected: boolean; onClick: () => void; kind: 'favorites' | 'category' | 'back'; tone?: string };
  const activeIndex = activeCategory ? categories.findIndex(c => c.id === activeCategory.id) : -1;
  const tiles: Tile[] = stepped && activeCategory
    ? [
      { key: 'back', label: tv('backToCategories'), selected: false, onClick: () => backToCategories?.(), kind: 'back' as const },
      {
        key: `c${activeCategory.id}`, label: activeCategory.name, tone: categoryToneOf(activeCategory, activeIndex),
        selected: !activeSubcategory, onClick: () => onCategorySelect(activeCategory), kind: 'category' as const,
      },
      ...visibleSubcategories.map((s, index) => ({
        key: `s${s.id}`, label: s.name, tone: categoryToneOf(s, activeIndex + index + 1),
        selected: activeSubcategory === s.id, onClick: () => onSubcategorySelect(s), kind: 'category' as const,
      })),
    ]
    : [
      ...(hasFavorites ? [{
        key: 'favorites', label: tv('favorites'), tone: 'amber',
        selected: !isSearching && selection === 'favorites', onClick: () => onFavoritesSelect?.(), kind: 'favorites' as const,
      }] : []),
      ...categories.map((c, index) => ({
        key: `c${c.id}`, label: c.name, tone: categoryToneOf(c, index),
        selected: !isSearching && selection === c.id, onClick: () => onCategorySelect(c), kind: 'category' as const,
      })),
    ];
  const columns = Math.max(2, categoryColumns);
  const allSelected = !isSearching && selection === 'all';
  const showAllTile = prefs.showAll && products.length > 0 && !stepped;
  const rows = prefs.categoryRows;
  // + 0.5rem: το εσωτερικό padding του πλέγματος (xv2-category-scroll), ώστε η τελευταία ορατή σειρά να μην κόβεται.
  const categoriesMaxHeight = `${rows * TILE_REM + (rows - 1) * GAP_REM + 0.5}rem`;

  // Η επιλεγμένη κατηγορία να φαίνεται και όταν το πλέγμα κυλά (αλλαγή από αναζήτηση/άλλη οθόνη).
  const selectedKey = tiles.find(tile => tile.selected)?.key ?? (allSelected ? 'all' : null);
  useEffect(() => {
    const el = categoriesRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [selectedKey]);

  const tileBase = 'xv2-category relative h-[3.25rem] px-3 rounded-lg border text-[15px] font-semibold transition-colors active:scale-[0.98] touch-manipulation flex items-center justify-center gap-1.5 min-w-0';
  const tileState = (selected: boolean) => selected
    ? 'bg-brand border-brand text-brand-fg'
    : 'bg-surface-2 border-line text-t2 hover:border-line-strong hover:text-t1';

  const size = tileSizing(productColumns);

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-surface-1">
      <div className="shrink-0 px-5">
        <div ref={categoriesRef} role="group" aria-label={tv('categories')} data-rows={rows}
          className={`grid ${getGridClass(columns)} gap-2 overflow-y-auto overscroll-contain xv2-category-scroll`}
          style={{ maxHeight: categoriesMaxHeight }}>
          {tiles.map(tile => (tile.kind === 'back' ? (
            <button key={tile.key} type="button" onClick={tile.onClick} title={tile.label}
              data-testid="catalog-back-to-categories"
              className={`${tileBase} bg-surface-1 border-line-strong text-t1 hover:bg-surface-3`}>
              <ArrowLeft aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span className="truncate">{tile.label}</span>
            </button>
          ) : (
            <button key={tile.key} type="button" onClick={tile.onClick} title={tile.label}
              aria-pressed={tile.selected}
              data-tone={prefs.categoryColor === 'none' ? undefined : tile.tone}
              data-color={prefs.categoryColor === 'none' ? undefined : prefs.categoryColor}
              className={`${tileBase} ${tileState(tile.selected)}`}>
              {tile.kind === 'favorites' && <Star aria-hidden="true" className="h-4 w-4 shrink-0" />}
              <span className="truncate">{tile.label}</span>
            </button>
          )))}
          {showAllTile && (
            <button type="button" onClick={() => (onAllProductsSelect ? onAllProductsSelect() : onSubcategorySelect(null))}
              aria-pressed={allSelected}
              className={`${tileBase} ${allSelected ? tileState(true) : 'bg-brand-soft border-brand-line text-brand hover:border-brand'}`}>
              <LayoutGrid aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span className="truncate">{tv('all')} ({products.length})</span>
            </button>
          )}
        </div>

        {/* Υποκατηγορίες ως δεύτερη σειρά — ΜΟΝΟ όταν ο γονέας δεν δίνει τρόπο επιστροφής στις κατηγορίες
            (αλλιώς είναι βήμα μέσα στο πλέγμα, βλ. stepped). */}
        {!isSearching && !stepped && activeCategory && visibleSubcategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {[{ id: null as number | null, name: tv('allInCategory') }, ...visibleSubcategories.map(s => ({ id: s.id as number | null, name: s.name }))].map(chip => {
              const selected = chip.id === null ? !activeSubcategory : activeSubcategory === chip.id;
              return (
                <button key={chip.id ?? 'all'} type="button" aria-pressed={selected}
                  onClick={() => (chip.id === null
                    ? onCategorySelect(activeCategory)
                    : onSubcategorySelect(visibleSubcategories.find(s => s.id === chip.id) || null))}
                  className={`h-9 px-3.5 rounded-full border text-sm font-medium transition-colors touch-manipulation ${selected
                    ? 'bg-brand-soft border-brand-line text-brand'
                    : 'bg-surface-2 border-line text-t2 hover:border-line-strong'}`}>
                  {chip.name}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-baseline justify-between gap-3 mt-5 mb-3">
          <h2 className="text-base font-semibold text-t1 truncate">{sectionTitle}</h2>
          {!loading && <span className="text-sm text-t3 shrink-0">{tv('productCount', { count: shownProducts.length })}</span>}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-t3 gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            {t('products:loadingProducts', { defaultValue: 'Loading products...' })}
          </div>
        ) : shownProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-t3">
            <Package aria-hidden="true" className="h-10 w-10 mb-3 text-t4" />
            <p className="font-medium">{stepped && !activeSubcategory ? tv('pickSubcategory') : tv('noProducts')}</p>
          </div>
        ) : (
          <div className={`grid ${getGridClass(productColumns)} gap-3`}>
            {shownProducts.map(product => {
              const price = getProductPrice(product);
              const fee = getPlasticFeeAddon(product);
              const showPrice = product.is_open_price || product.is_weight_variable || price > 0;
              const unit = product.selling_unit === 'LT' ? 'lt' : 'kg';
              const inBasket = basketCounts?.[product.id] || 0;
              return (
                <button
                  key={product.id}
                  type="button"
                  data-testid={`product-tile-${product.id}`}
                  data-tone={catalogTone(product.color) ?? undefined}
                  data-in-basket={inBasket > 0 ? 'true' : undefined}
                  onClick={() => onProductClick(product)}
                  onMouseDown={() => onProductMouseDown(product)}
                  onMouseUp={onProductMouseUp}
                  onMouseLeave={onProductMouseUp}
                  onTouchStart={() => onProductMouseDown(product)}
                  onTouchEnd={onProductMouseUp}
                  onPointerUp={e => keepTouchClickOn(e, e.currentTarget)}
                  className={`xv2-product relative text-center rounded-xl border bg-surface-2 active:scale-[0.98] transition-[transform,border-color] duration-100 flex flex-col items-center justify-center gap-1.5 ${size.pad} ${size.minH} ${
                    inBasket ? 'border-brand-line' : 'border-line hover:border-line-strong'
                  }`}
                >
                  {inBasket > 0 && (
                    // key = ποσότητα: ξαναστήνεται σε κάθε προσθήκη, άρα «χτυπά» το ζουμ κάθε φορά.
                    <span key={inBasket} data-testid={`product-count-${product.id}`}
                      className="absolute -top-2 -right-2 min-w-[1.75rem] h-7 px-1.5 rounded-full bg-brand text-brand-fg text-sm font-bold tabular-nums flex items-center justify-center ring-2 ring-surface-1 animate-in zoom-in-50 duration-200">
                      ×{inBasket}
                    </span>
                  )}
                  <span className={`font-semibold text-t1 leading-snug line-clamp-2 break-words w-full ${size.name}`}>{product.name}</span>
                  {(showPrice || (fee > 0 && !product.is_open_price)) && (
                    <span className="min-w-0">
                      {showPrice && (
                        product.is_open_price ? (
                          <span className={`font-semibold text-t2 ${size.price}`}>
                            {tv('openPrice')}
                            {/* «Βάρος + τιμή κιλού»: ελεύθερη τιμή ΑΝΑ κιλό/λίτρο. */}
                            {product.is_weight_variable && <span className="text-xs font-medium text-t3">/{unit}</span>}
                          </span>
                        ) : (
                          <span className={`font-bold text-t1 tabular-nums whitespace-nowrap ${size.price}`}>
                            {formatEuro(price)}
                            {product.is_weight_variable && <span className="text-xs font-medium text-t3">/{unit}</span>}
                          </span>
                        )
                      )}
                      {fee > 0 && !product.is_open_price && (
                        <span className="block text-[11px] text-t3 mt-0.5">+ {formatEuro(fee)} τέλη</span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCatalogV2;
